import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cloud, ExternalLink, Laptop, Trash2 } from 'lucide-react';
import { Row } from './settingsPrimitives';
import { ConfirmModal } from './ui/Modal';
import { useAuthStore } from '../store/authStore';
import {
  CONSENT_CHANGED_EVENT,
  choiceOf,
  deleteCloudCopy,
  keepOnDevice,
  readConsent,
  storeInCloud,
} from '../services/vaultCloudConsent';
import { ensureVaultSync } from '../services/vaultSync';
import { openExternal } from '../utils/openExternal';
import { VAULT_WEB_URL } from '../config/externalLinks';
import VaultCapMeter from './VaultCapMeter';
import VaultCapKept from './VaultCapKept';
import VaultSyncFacts from './VaultSyncFacts';
import './Vault.css';

/**
 * Settings → Extensions → Vault → "Cloud copy" (Standalone vault cards 6.3' /
 * 6.2): where the vault lives, changeable at any time, and "Delete my cloud
 * copy" — every encrypted row removed from the server; the vault on this and
 * every other device stays as it is.
 */
export default function VaultCloudSection() {
  const { t } = useTranslation();
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(null);

  const read = useCallback(async () => {
    try {
      setConsent(await readConsent());
    } catch {
      setConsent(null);
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    read();
    window.addEventListener(CONSENT_CHANGED_EVENT, read);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, read);
  }, [signedIn, read]);

  const run = async (fn, after) => {
    setBusy(true);
    setError('');
    try {
      const out = await fn();
      if (after) after(out);
      await read();
    } catch (e) {
      const msg = String(e?.message || '');
      let text = t('vault.cloudChoiceFailed', 'That did not work. Check your connection and try again.');
      if (msg === 'reauth_cancelled' || msg === 'account_changed') {
        text = t('vault.cloudDeleteReauth', 'Sign in again to confirm it is you. Nothing was deleted.');
      } else if (msg === 'second_factor_required') {
        text = t(
          'vault.cloudDeleteSecondFactor',
          'Sign in with your second step (authenticator app or passkey) to delete the cloud copy. Nothing was deleted.'
        );
      }
      setError(text);
    }
    setBusy(false);
  };

  if (!signedIn) {
    return (
      <div className="vault-passkey-hint" style={{ marginLeft: 0 }}>
        {t('vault.cloudSignedOut', 'Sign in to keep an encrypted copy of your vault in your Apiboo account.')}
      </div>
    );
  }

  const choice = choiceOf(consent);
  const stored = (consent?.items || 0) > 0;

  return (
    <div data-testid="vault-cloud-section">
      <Row
        label={
          choice === 'cloud'
            ? t('vault.cloudStatusCloud', 'Stored encrypted in your Apiboo cloud')
            : choice === 'device'
              ? t('vault.cloudStatusDevice', 'On this device only')
              : t('vault.cloudStatusAsk', 'Not chosen yet')
        }
        desc={
          choice === 'cloud'
            ? t('vault.cloudStatusCloudDesc', 'Your other devices, the browser extension and the Android app get it from there. Apiboo cannot read it.')
            : t('vault.cloudStatusDeviceDesc', 'Nothing is uploaded. Other devices and the browser extension without this app cannot use this vault.')
        }
      >
        {choice === 'cloud' ? (
          <button type="button" className="settings-action-btn" disabled={busy} onClick={() => run(keepOnDevice)}>
            <Laptop size={13} />
            {t('vault.cloudDevice', 'Keep on this device only')}
          </button>
        ) : (
          <button
            type="button"
            className="settings-action-btn"
            disabled={busy}
            onClick={() => run(storeInCloud, () => ensureVaultSync())}
          >
            <Cloud size={13} />
            {t('vault.cloudStoreShort', 'Store in my Apiboo cloud')}
          </button>
        )}
      </Row>
      {choice === 'cloud' && <VaultSyncFacts />}
      <VaultCapMeter consent={consent} />
      <VaultCapKept consent={consent} />
      {stored && (
        <Row
          label={t('vault.webVaultLink', 'Open in the browser')}
          desc={t(
            'vault.webVaultLinkDesc',
            'vault.apiboo.com shows this vault on any computer: sign in with your Apiboo account, then unlock with your master password. It opens in your browser.'
          )}
        >
          <button type="button" className="settings-resource-btn" onClick={() => openExternal(VAULT_WEB_URL)}>
            vault.apiboo.com <ExternalLink size={12} />
          </button>
        </Row>
      )}
      {stored && (
        <Row
          label={t('vault.cloudDelete', 'Delete my cloud copy')}
          desc={t(
            'vault.cloudDeleteDesc',
            'Removes every encrypted vault item from your Apiboo account for good. The vault on this computer and your other devices stays as it is.'
          )}
        >
          <button type="button" className="settings-action-btn" disabled={busy} onClick={() => setConfirmDelete(true)}>
            <Trash2 size={13} />
            {t('vault.cloudDeleteAction', 'Delete cloud copy')}
          </button>
        </Row>
      )}
      {deleted !== null && (
        <div className="vault-share-success">
          {t('vault.cloudDeleted', 'Your cloud copy is deleted. This vault now stays on this device only.')}
        </div>
      )}
      {error && <div className="vault-modal-error">{error}</div>}
      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          run(() => deleteCloudCopy(), (out) => setDeleted(out?.deleted ?? 0));
        }}
        title={t('vault.cloudDeleteConfirmTitle', 'Delete your cloud copy?')}
        message={t(
          'vault.cloudDeleteConfirmBody',
          'Every encrypted vault item is removed from your Apiboo account. Devices that only had it from the cloud keep what they already have. This cannot be undone.'
        )}
        confirmText={t('vault.cloudDeleteAction', 'Delete cloud copy')}
        type="danger"
      />
    </div>
  );
}
